import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/caesar";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionLabel, Loading, EmptyState, Pill } from "@/components/common";
import { toast } from "sonner";
import { BarChart3, Send, RefreshCw, Trophy, Save } from "lucide-react";

const RANGES = [["today", "Today"], ["yesterday", "Yesterday"], ["week", "This Week"], ["last_week", "Last Week"], ["month", "This Month"]];
const POSTF = [["posts_published", "Posts"], ["reels", "Reels/Shorts"], ["stories", "Stories"], ["videos", "Videos"], ["community_posts", "Community"], ["posts_scheduled", "Scheduled"], ["posts_pending", "Pending"]];
const DMF = [["dms_new", "New Outbound DMs"], ["dms_replies", "Replies"], ["dms_followups", "Follow-ups"], ["wa_messages", "WhatsApp Msgs"], ["comments_outbound", "Outbound Comments"]];

function KPI({ label, value, testId }) {
  return (
    <div className="bg-surface border border-edge rounded-xl p-4" data-testid={testId}>
      <div className="label-caps text-[0.6rem] text-[#A3A3A3]">{label}</div>
      <div className="font-display text-3xl font-extrabold text-gold mt-1">{value}</div>
    </div>
  );
}

function Bar({ pct }) {
  const p = Math.min(100, pct || 0);
  const color = p >= 100 ? "#10B981" : p >= 75 ? "#D4AF37" : p >= 50 ? "#F59E0B" : "#E11D48";
  return <div className="h-2 bg-ink rounded-full overflow-hidden"><div style={{ width: `${p}%`, background: color }} className="h-full" /></div>;
}

function LogForm({ platforms, members, onSaved }) {
  const [pf, setPf] = useState("instagram");
  const [member, setMember] = useState("");
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setVals((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/social/log", { platform: pf, team_member_id: member || undefined, ...vals });
      toast.success("Activity logged (Manual Entry)");
      setVals({});
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-surface border border-edge rounded-xl p-4 space-y-3" data-testid="log-form">
      <div className="flex items-center gap-2 flex-wrap">
        <select data-testid="log-platform" value={pf} onChange={(e) => setPf(e.target.value)} className="h-10 rounded-md bg-ink border border-edge px-3 text-sm">
          {platforms.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
        {members && (
          <select data-testid="log-member" value={member} onChange={(e) => setMember(e.target.value)} className="h-10 rounded-md bg-ink border border-edge px-3 text-sm">
            <option value="">Myself</option>
            {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
          </select>
        )}
        <span className="text-xs text-samber">MANUAL ENTRY</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {[...POSTF, ...DMF].map(([k, label]) => (
          <label key={k} className="text-xs text-[#A3A3A3]">
            {label}
            <input data-testid={`log-${k}`} type="number" min="0" value={vals[k] ?? ""} onChange={(e) => set(k, e.target.value)} placeholder="0" className="mt-1 w-full h-9 rounded-md bg-ink border border-edge px-2 text-[#FAFAFA]" />
          </label>
        ))}
      </div>
      <button data-testid="log-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gold text-ink font-bold disabled:opacity-60"><Save size={15} /> {busy ? "Saving…" : "Save Activity"}</button>
    </div>
  );
}

export default function SocialActivity() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [cfg, setCfg] = useState(null);
  const [me, setMe] = useState(null);
  const [summary, setSummary] = useState(null);
  const [range, setRange] = useState("today");
  const [members, setMembers] = useState([]);
  const [reports, setReports] = useState([]);
  const [settings, setSettings] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [goal, setGoal] = useState({ team_member_id: "default", platform: "instagram", post_goal: "", dm_goal: "", followup_goal: "" });
  const [attr, setAttr] = useState(null);
  const [outreach, setOutreach] = useState([]);
  const [oform, setOform] = useState({ platform: "instagram", stage: "dm_initiated", amount: "", member: "" });

  const loadMe = useCallback(() => api.get("/social/my").then(({ data }) => setMe(data)).catch(() => {}), []);
  const loadSummary = useCallback((r) => api.get(`/social/activity-summary?range=${r}`).then(({ data }) => setSummary(data)).catch(() => {}), []);

  useEffect(() => {
    api.get("/social/config").then(({ data }) => setCfg(data));
    loadMe();
    if (isAdmin) {
      loadSummary(range);
      api.get("/admin/users").then(({ data }) => setMembers(data.filter((u) => u.active !== false)));
      api.get("/social/reports").then(({ data }) => setReports(data));
      api.get("/social/settings").then(({ data }) => setSettings(data));
      api.get("/social/attribution").then(({ data }) => setAttr(data));
      api.get("/social/outreach").then(({ data }) => setOutreach(data));
    }
  }, [isAdmin, loadMe, loadSummary, range]);

  const generate = async () => {
    setGenBusy(true);
    try {
      const { data } = await api.post("/social/report/generate", {});
      const es = data.email_status || {};
      toast.success(`Report saved. Email: ${es.delivered?.length || 0} sent, ${es.failed?.length || 0} failed`);
      api.get("/social/reports").then(({ data }) => setReports(data));
    } catch { toast.error("Could not generate report"); } finally { setGenBusy(false); }
  };

  const saveGoal = async () => {
    try { await api.put("/social/goals", goal); toast.success("Goal saved"); loadSummary(range); loadMe(); }
    catch { toast.error("Could not save goal"); }
  };
  const saveRecipients = async (val) => {
    const recipients = val.split(",").map((s) => s.trim()).filter(Boolean);
    try { const { data } = await api.put("/social/settings", { recipients }); setSettings(data); toast.success("Recipients updated"); }
    catch { toast.error("Could not save"); }
  };
  const reloadAttr = () => { api.get("/social/attribution").then(({ data }) => setAttr(data)); api.get("/social/outreach").then(({ data }) => setOutreach(data)); };
  const addOutreach = async () => {
    try { await api.post("/social/outreach", { platform: oform.platform, stage: oform.stage, amount: oform.amount || 0, team_member_id: oform.member || undefined }); toast.success("Outreach added"); setOform({ ...oform, amount: "" }); reloadAttr(); }
    catch { toast.error("Could not add"); }
  };
  const advance = async (id, stage, amount) => { try { await api.put(`/social/outreach/${id}`, { stage, amount: amount || 0 }); reloadAttr(); } catch { toast.error("Update failed"); } };

  if (!cfg || !me) return <Loading label="Loading social activity…" />;

  return (
    <div className="space-y-8" data-testid="social-activity-page">
      <PageHeader testId="social-report-header" title="Social Activity Report" subtitle="Daily outbound accountability across all platforms — new DMs tracked separately from replies." />

      {/* Personal performance */}
      <div>
        <SectionLabel>My Social Performance — Today</SectionLabel>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KPI label="Posts" value={me.posts || 0} testId="my-posts" />
          <KPI label="New Outbound DMs" value={me.dms_new || 0} testId="my-dms" />
          <KPI label="Follow-ups" value={me.followups || 0} testId="my-followups" />
          <KPI label="DM Goal %" value={`${me.dm_completion || 0}% ${me.status || ""}`} testId="my-completion" />
        </div>
        <div className="mt-3 bg-surface border border-edge rounded-xl p-4 space-y-2">
          <Bar pct={me.dm_completion} />
          <p className="text-sm text-gold" data-testid="my-need-dms">You need <b>{me.dm_needed || 0}</b> more outbound DMs to reach today's goal.</p>
          <p className="text-sm text-[#A3A3A3]" data-testid="my-need-posts">You need <b>{me.post_needed || 0}</b> more posts to reach today's goal.</p>
        </div>
      </div>

      {/* Log form */}
      <div>
        <SectionLabel>Log Today's Activity</SectionLabel>
        <LogForm platforms={cfg.platforms} members={isAdmin ? members : null} onSaved={() => { loadMe(); if (isAdmin) loadSummary(range); }} />
      </div>

      {isAdmin && summary && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            {RANGES.map(([v, l]) => (
              <button key={v} data-testid={`range-${v}`} onClick={() => setRange(v)} className={`h-9 px-3 rounded-lg text-xs font-semibold border ${range === v ? "bg-gold text-ink border-gold" : "border-edge text-[#A3A3A3]"}`}>{l}</button>
            ))}
            <button data-testid="refresh-summary" onClick={() => loadSummary(range)} className="ml-auto inline-flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-gold"><RefreshCw size={14} /> Refresh</button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <KPI label="Total Posts" value={summary.company.posts} testId="co-posts" />
            <KPI label="New Outbound DMs" value={summary.company.dms_new} testId="co-dms" />
            <KPI label="Follow-ups" value={summary.company.followups} testId="co-followups" />
            <KPI label="Total Activities" value={summary.company.activities} testId="co-activities" />
            <KPI label="Goal Completion" value={`${summary.company.completion}%`} testId="co-completion" />
          </div>

          {!summary.has_data ? (
            <EmptyState testId="no-data" title="No activity logged yet" hint="Team members can log their daily posts and outbound DMs above." />
          ) : (
            <>
              <div>
                <SectionLabel>Today's Outreach Leaderboard</SectionLabel>
                <div className="space-y-2" data-testid="leaderboard">
                  {summary.leaderboard.map((m, i) => (
                    <div key={m.team_member_id} className="bg-surface border border-edge rounded-xl px-4 py-3 flex items-center gap-3" data-testid={`leader-${i}`}>
                      <span className="font-display text-xl font-extrabold text-gold w-6">{i === 0 ? <Trophy size={18} /> : i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between"><span className="font-semibold">{m.team_member_name}</span><span className="text-sm">{m.status} {m.completion}%</span></div>
                        <div className="text-xs text-[#A3A3A3]">{m.posts} posts · {m.dms_new} new DMs · {m.followups} follow-ups</div>
                        <div className="mt-1.5"><Bar pct={m.completion} /></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <SectionLabel>By Platform</SectionLabel>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="platform-cards">
                  {summary.platforms.filter((p) => p.activities > 0).map((p) => (
                    <div key={p.platform} className="bg-surface border border-edge rounded-xl p-4">
                      <div className="label-caps text-[0.6rem] text-gold">{p.platform}</div>
                      <div className="text-sm mt-1">{p.posts} posts</div>
                      <div className="text-sm text-sgreen">{p.dms_new} DMs</div>
                    </div>
                  ))}
                </div>
              </div>

              {(summary.missed_goals.length > 0 || summary.zero_outreach.length > 0) && (
                <div className="bg-surface border border-sred/40 rounded-xl p-4 text-sm" data-testid="missed-goals">
                  <div className="label-caps text-sred mb-1">Missed Goals</div>
                  <p>Below target: {summary.missed_goals.join(", ") || "None"}</p>
                  <p>Zero outbound DMs: {summary.zero_outreach.join(", ") || "None"}</p>
                  {summary.zero_platforms.length > 0 && <p>Zero-activity platforms: {summary.zero_platforms.join(", ")}</p>}
                </div>
              )}
            </>
          )}

          {/* Admin: report + settings */}
          <div>
            <SectionLabel>8 PM Executive Report</SectionLabel>
            <div className="bg-surface border border-gold/30 rounded-xl p-4 space-y-3">
              <button data-testid="generate-report" onClick={generate} disabled={genBusy} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gold text-ink font-bold disabled:opacity-60"><Send size={15} /> {genBusy ? "Generating…" : "Generate & Send Report Now"}</button>
              <label className="block text-xs text-[#A3A3A3]">Recipient emails (comma-separated)
                <input data-testid="report-recipients" defaultValue={(settings?.recipients || []).join(", ")} onBlur={(e) => saveRecipients(e.target.value)} className="mt-1 w-full h-10 rounded-md bg-ink border border-edge px-3 text-[#FAFAFA]" />
              </label>
              <p className="text-[0.7rem] text-[#5a5a5a]">Auto-sends daily at 8:00 PM America/Chicago. Email delivers on the published site.</p>
              {reports.length > 0 && (
                <div data-testid="report-history" className="pt-2">
                  <div className="label-caps text-[0.6rem] mb-1">Report History</div>
                  {reports.slice(0, 7).map((r) => (
                    <div key={r.id} className="flex items-center justify-between text-sm py-1 border-b border-edge/50">
                      <span>{r.date}</span>
                      <span className="text-[#A3A3A3]">{r.company?.dms_new || 0} DMs · {(r.email_status?.delivered?.length || 0)} emailed{r.email_status?.failed?.length ? ` · ${r.email_status.failed.length} failed` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <SectionLabel>Daily Goals (Admin)</SectionLabel>
            <div className="bg-surface border border-edge rounded-xl p-4 space-y-3" data-testid="goals-editor">
              <div className="flex flex-wrap gap-2">
                <select data-testid="goal-member" value={goal.team_member_id} onChange={(e) => setGoal({ ...goal, team_member_id: e.target.value })} className="h-10 rounded-md bg-ink border border-edge px-3 text-sm">
                  <option value="default">All members (default)</option>
                  {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
                </select>
                <select data-testid="goal-platform" value={goal.platform} onChange={(e) => setGoal({ ...goal, platform: e.target.value })} className="h-10 rounded-md bg-ink border border-edge px-3 text-sm">
                  {cfg.platforms.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
                <input data-testid="goal-post" type="number" min="0" placeholder="Posts" value={goal.post_goal} onChange={(e) => setGoal({ ...goal, post_goal: e.target.value })} className="h-10 w-24 rounded-md bg-ink border border-edge px-2" />
                <input data-testid="goal-dm" type="number" min="0" placeholder="DMs" value={goal.dm_goal} onChange={(e) => setGoal({ ...goal, dm_goal: e.target.value })} className="h-10 w-24 rounded-md bg-ink border border-edge px-2" />
                <input data-testid="goal-followup" type="number" min="0" placeholder="Follow-ups" value={goal.followup_goal} onChange={(e) => setGoal({ ...goal, followup_goal: e.target.value })} className="h-10 w-28 rounded-md bg-ink border border-edge px-2" />
                <button data-testid="goal-save" onClick={saveGoal} className="h-10 px-4 rounded-lg bg-gold text-ink font-bold">Save Goal</button>
              </div>
              <p className="text-[0.7rem] text-[#5a5a5a]">Set a per-platform default for everyone, or override for a specific member.</p>
            </div>
          </div>

          <div>
            <SectionLabel>Outreach Pipeline & Revenue</SectionLabel>
            <div className="bg-surface border border-edge rounded-xl p-4 space-y-3" data-testid="attribution-panel">
              {attr && (
                <>
                  <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                    {cfg.stages.map((s) => (
                      <div key={s} className="bg-ink border border-edge rounded-lg p-2 text-center">
                        <div className="label-caps text-[0.45rem] text-[#A3A3A3]">{s.replace(/_/g, " ")}</div>
                        <div className="font-num text-lg text-gold">{attr.funnel?.[s] || 0}</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-sm">Total attributed revenue: <b className="text-sgreen" data-testid="attr-revenue">${(attr.total_revenue || 0).toLocaleString()}</b> from {attr.total_records || 0} conversations.</div>
                  {attr.by_platform?.length > 0 && <div className="text-xs text-[#A3A3A3]">By platform: {attr.by_platform.map((p) => `${p.platform} $${p.revenue.toLocaleString()}`).join(" · ")}</div>}
                </>
              )}
              <div className="flex flex-wrap gap-2 items-center pt-2 border-t border-edge/50">
                <select data-testid="o-platform" value={oform.platform} onChange={(e) => setOform({ ...oform, platform: e.target.value })} className="h-9 rounded-md bg-ink border border-edge px-2 text-sm">{cfg.platforms.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}</select>
                <select data-testid="o-stage" value={oform.stage} onChange={(e) => setOform({ ...oform, stage: e.target.value })} className="h-9 rounded-md bg-ink border border-edge px-2 text-sm">{cfg.stages.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select>
                <input data-testid="o-amount" type="number" min="0" placeholder="$ amount" value={oform.amount} onChange={(e) => setOform({ ...oform, amount: e.target.value })} className="h-9 w-28 rounded-md bg-ink border border-edge px-2" />
                <button data-testid="o-add" onClick={addOutreach} className="h-9 px-3 rounded-lg bg-gold text-ink font-bold text-sm">Add Outreach</button>
              </div>
              {outreach.slice(0, 8).map((o) => (
                <div key={o.id} className="flex items-center gap-2 text-xs border-b border-edge/40 py-1">
                  <span className="flex-1 truncate">{o.team_member_name} · {o.platform}</span>
                  <select value={o.stage} onChange={(e) => advance(o.id, e.target.value, o.amount)} className="h-8 rounded bg-ink border border-edge px-1">{cfg.stages.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select>
                  <span className="text-sgreen w-16 text-right">${(o.amount || 0).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
